import { type IResponseApiMeta } from 'shared/api'
import { type IFiltersListGroups } from 'entities/Contacts/api/types'
import { type IResponseFilterSegment } from 'entities/Segment'
import { type IParamsSendFrom } from 'entities/SendFrom'
import type { RequestAttachment } from 'entities/Attachment/model/RequestAttachment.ts'
import { type IAgentActionSettings } from 'entities/AgentActions'

export enum SurveysType {
  NPS = 1,
  CSAT = 2,
  XSAT = 3,
  CES = 4,
  PMF = 5,
  Custom = 6,
}

export enum SurveysStatus {
  Draft = 0,
  Active = 1,
  Disabled = 2,
}

export const SurveysFollowUpAssertiveness = {
  Subtle: 'subtle',
  Steady: 'steady',
  Assertive: 'assertive',
} as const

export type ISurveysFollowUpAssertiveness =
  (typeof SurveysFollowUpAssertiveness)[keyof typeof SurveysFollowUpAssertiveness]

export type IParamsCreateSurvey = {
  type: SurveysType
  name: string
  is_active: boolean
  send_from: IParamsSendFrom | null
  filters: IFiltersListGroups | null
  exclude_filters: IFiltersListGroups | null
  question: {
    company_name: string
    text: string
  }
  settings: ISurveySettings
}

export type ISurveySettings = {
  allow_re_entering: {
    value: boolean
  }
  score_elaboration: {
    value: boolean
  }
  score_elaboration_per_group?: {
    value: boolean
  }
  score_elaboration_message: {
    value: {
      message: string
      media_url: RequestAttachment[]
    }
  }
  score_elaboration_promoters?: {
    value: {
      message: string
      media_url: RequestAttachment[]
    } | null
  }
  score_elaboration_passives?: {
    value: {
      message: string
      media_url: RequestAttachment[]
    } | null
  }
  score_elaboration_detractors?: {
    value: {
      message: string
      media_url: RequestAttachment[]
    } | null
  }
  closing_message: {
    value: boolean
  }
  closing_message_per_group?: {
    value: boolean
  }
  closing_message_value: {
    value: {
      message: string
      media_url: RequestAttachment[]
    }
  }
  closing_message_promoters?: {
    value: {
      message: string
      media_url: RequestAttachment[]
    } | null
  }
  closing_message_passives?: {
    value: {
      message: string
      media_url: RequestAttachment[]
    } | null
  }
  closing_message_detractors?: {
    value: {
      message: string
      media_url: RequestAttachment[]
    } | null
  }
  success_action: {
    value: IAgentActionSettings[] | null
  }
  success_action_per_group?: {
    value: boolean
  }
  success_action_promoters?: {
    value: IAgentActionSettings[] | null
  }
  success_action_passives?: {
    value: IAgentActionSettings[] | null
  }
  success_action_detractors?: {
    value: IAgentActionSettings[] | null
  }
  fallback_auto_follow_up_window?: {
    value: number
  }
  fallback_auto_follow_up?: {
    value: boolean
  }
  fallback_end_session?: {
    value: number
  }
  fallback_auto_follow_up_frequency?: {
    value: ISurveysFollowUpAssertiveness
  }
  fallback_action: {
    value: IAgentActionSettings[] | null
  }
}

export type IResponseGetSurveys = {
  data: IResponseGetSurvey[]
  meta: IResponseApiMeta
}

export type IParamsGetSurveys = {
  page?: number
  limit?: number
  'filters[name]'?: string
  'filters[status]'?: SurveysStatus
}

export type IParamsEnrolmentSurvey = {
  id: number
  data?: {
    contact_ids?: number[]
    apply_filters: boolean
    contact_filters?: IResponseFilterSegment[]
    search?: string
    apply_to_all?: boolean
  }
}

export type IResponseEnrolmentSurvey = {
  contact_ids: number[]
  apply_filters: boolean
}

export type IResponseCreateSurvey = IParamsCreateSurvey & {
  id: number
  status: SurveysStatus
}

export type IResponseGetSurvey = {
  id: number
  name: string
  is_active: boolean
  type: SurveysType
  status: SurveysStatus
  filters: IFiltersListGroups | null
  exclude_filters: IFiltersListGroups | null
  nps_score: number | null
  total_sent: number
  valid_replies: number
  invalid_replies: number
  undelivered: number | null
  expired: number | null
  created_at: string
  updated_at: string
}

export type ISurveyExecutionOutcome =
  | 'pending'
  | 'timeout'
  | 'valid'
  | 'invalid'
  | 'undelivered'
  | 'expired'

export type ISurveyNpsGroup = 'promoter' | 'passive' | 'detractor'

export type IResponseSurveyExecution = {
  survey_id: number
  survey_name: string
  sent_at: string
  outcome: ISurveyExecutionOutcome
  nps_score: number | null
  nps_group: ISurveyNpsGroup | null
}

export type IResponseGetSurveyExecutions = {
  data: IResponseSurveyExecution[]
}

export type IResponseEventContactSurveyExecutionCreated = IResponseSurveyExecution & {
  contact_id: number
}

export type IResponseFullSurvey = IResponseCreateSurvey & IResponseSurveyDetails

export type IResponseSurveyDetails = {
  total_contacts_count: number
  valid_replies_count: number
  invalid_replies_count: number
  undelivered_count: number
  expired_count: number
  nps_score: number
  promoters_count: number
  promoters_percent: number
  passives_count: number
  passives_percent: number
  detractors_count: number
  detractors_percent: number
  created_at: string
}
